import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { LoginService } from '../../auth/login.service';
import { isPortalRecoveryFlow } from '../utils/recovery-flow.utils';

export const LoginGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  // Recovery flow takes priority over the "already logged in" redirect:
  // the user just clicked the Supabase recovery / invite link and the
  // URL hash carries the only copy of the access_token we'll ever see.
  // Bouncing them to /profile drops that hash and would force them
  // through the 2FA setup modal cascade (which then fires protected
  // backend calls without a fresh token, surfacing as "No authorization
  // header provided"). Always let /reset-password render, and drop any
  // stale Portal session in localStorage so the LayoutComponent doesn't
  // auto-call /users/me on top of the password-set screen.
  if (isPortalRecoveryFlow(state.url)) {
    if (loginService.isAuthenticated()) {
      loginService.clearToken();
    }
    return true;
  }

  // A fresh tab may have a valid cookie without any cached profile.
  // Prove the server session before showing login, regardless of Remember.
  return loginService.fetchCurrentUser().pipe(
    map(() => router.createUrlTree(['/profile'])),
    // A temporary /me outage must not strand a known session on the login form.
    // Definitive 401s already cleared the cache in AuthInterceptor.
    catchError(() =>
      of(loginService.hasUsableSession() ? router.createUrlTree(['/profile']) : true)
    )
  );
};
